/* eslint-disable camelcase */
import { ActionContext } from 'vuex';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { RelativeRootGetters } from '@store/types';
import {
    BetBuilderOdd,
    BetBuilderOddStatus,
} from '../../types/BetBuilderOdd';
import { BetBuilderPreviewSummary } from '../../types/BetBuilderPreview';
import { BetBuilderMarket } from '../../types/BetBuilderMarket';
import { logger } from '@superbet-group/web.lib.utilities';
import {
    ArticleEventCardSource,
    CompetitionEventCardSource,
    EventCardSource,
    FavouritesEventCardSource,
    HomeEventCardSource,
    SearchEventCardSource,
    SportsEventCardSource,
    SuperAdvantageSource,
} from '@src/app/enums';
import {
    EventCardLocation,
    SelectionSourceScreen,
} from '@superbet-group/web.types';
import Odd from '@core/models/offer/Odd';
import { getAnalyticsEventId } from '@sportOfferFederation/bet-builder/utils/getAnalyticsEventId';

export const SuperbetsLocation = {
    Carousel: 'Carousel',
    MarketGroup: 'MarketGroup',
    Sport_PopularSuperbets: 'PopularSuperbets',
    ArticleDetails: 'ArticleDetails',
    Home_PopularSuperbets: 'PopularSuperbetsHome',
    Unknown: 'Unknown',
} as const;
export type SuperbetsLocation = typeof SuperbetsLocation[keyof typeof SuperbetsLocation];

export const OfferStatus = {
    Prematch: 'prematch',
    Live: 'live',
    Postmatch: 'postmatch',
} as const;
export type OfferStatus = typeof OfferStatus[keyof typeof OfferStatus];

function getLocationFromEventCardSource(source: EventCardSource): EventCardLocation {
    switch (source) {
        case ArticleEventCardSource.Default:
        case ArticleEventCardSource.Segment:
            return EventCardLocation.News;
        case SportsEventCardSource.PopularEvents:
            return EventCardLocation.Sport_PopularEvents;
        case SportsEventCardSource.Default:
            return EventCardLocation.Sport_Calendar;
        case SearchEventCardSource.Default:
            return EventCardLocation.Search;
        case HomeEventCardSource.SuperAdvantage:
            return EventCardLocation.Home_SuperAdvantage;
        case HomeEventCardSource.SuperOdds:
            return EventCardLocation.Home_SuperKvota;
        case HomeEventCardSource.SuperExtra:
            return EventCardLocation.Home_SuperExtra;
        case HomeEventCardSource.Top10:
            return EventCardLocation.Home_Top10;
        case FavouritesEventCardSource.Default:
            return EventCardLocation.Favorite;
        case CompetitionEventCardSource.Default:
            return EventCardLocation.Competition;
        case SuperAdvantageSource.Default:
            return EventCardLocation.SuperAdvantage;
        default:
            return EventCardLocation.Unknown;
    }
}

const superbetsSourceMapper = (source: SelectionSourceScreen): SuperbetsLocation => {
    switch (source) {
        case SelectionSourceScreen.SportsCalendarPopularSuperbets:
            return SuperbetsLocation.Sport_PopularSuperbets;
        case SelectionSourceScreen.HomePopularSuperbets:
            return SuperbetsLocation.Home_PopularSuperbets;
        case SelectionSourceScreen.NewsArticleArticleBody:
        case SelectionSourceScreen.NewsArticleRelatedMatch:
            return SuperbetsLocation.ArticleDetails;
        default:
            return SuperbetsLocation.Unknown;
    }
};

export const betBuilderAnalyticsStore = {
    namespaced: true,
    state: {},
    actions: {
        matchDetailsOddsLoaded(context: AnalyticsContext, payload: MatchDetailsOddsPayload): void {
            const { eventId, ...rest } = payload;

            try {
                AnalyticsSDK.getInstance().log('Match_Details_Odds_Loaded', {
                    has_superbets: rest.hasBetBuilder ? 'TRUE' : 'FALSE',
                    has_bet_builder: rest.hasBetBuilder ? 'TRUE' : 'FALSE',
                    ...constructPayload(context, eventId, rest),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.matchDetailsOddsLoaded"';
                logger.error(e as Error);
            }
        },
        marketGroupClicked(context: AnalyticsContext, payload: MarketGroupPayload): void {
            const { eventId, ...rest } = payload;
            try {
                AnalyticsSDK.getInstance().log('Market_Group_Filter', {
                    MarketGroupId: rest.marketGroupId.toString(),
                    MarketGroupName: rest.marketGroupName,
                    ...constructPayload(context, eventId, rest),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.marketGroupClicked"';
                logger.error(e as Error);
            }
        },
        betBuilderEventCardLabelClicked(context: AnalyticsContext, { eventId, source }: EventCardPayload): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Event_Card_Label', {
                    location: getLocationFromEventCardSource(source),
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderEventCardLabelClicked"';
                logger.error(e as Error);
            }
        },
        betBuilderOboardingBuildNowClicked(context: AnalyticsContext, eventId: number): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Onboarding_Build_Now', constructPayload(context, eventId));
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderOnboardingBuildNowClicked"';
                logger.error(e as Error);
            }
        },
        betBuilderCrossSellingFromSuperbetsBannerClicked(context: AnalyticsContext, eventId: number): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Cross_Selling_SB_Banner', constructPayload(context, eventId));
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderCrossSellingFromSuperbetsBannerClicked"';
                logger.error(e as Error);
            }
        },
        betBuilderBannerClicked(context: AnalyticsContext, eventId: number): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Banner', constructPayload(context, eventId));
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderBannerClicked"';
                logger.error(e as Error);
            }
        },
        betBuilderBannerDismissed(context: AnalyticsContext, eventId: number): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Banner_Dismiss', constructPayload(context, eventId));
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderBannerDismissed"';
                logger.error(e as Error);
            }
        },
        betBuilderInfoBannerClicked(context: AnalyticsContext, eventId: number): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Onboarding_Banner', constructPayload(context, eventId));
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderInfoBannerClicked"';
                logger.error(e as Error);
            }
        },
        betBuilderInfoBannerClosed(context: AnalyticsContext, eventId: number): void {
            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Info_Banner_Close', constructPayload(context, eventId));
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderInfoBannerClosed"';
                logger.error(e as Error);
            }
        },
        marketInfoClicked(context: AnalyticsContext, payload: MarketInfoPayload): void {
            const { eventId, market, ...rest } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Bet_Info', {
                    MarketId: market.id,
                    MarketName: market.name,
                    new_state: rest.isOpened ? 'opened' : 'closed',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderInfoClicked"';
                logger.error(e as Error);
            }
        },
        marketFavouriteClicked(context: AnalyticsContext, payload: MarketFavouritePayload): void {
            const { eventId, market, ...rest } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Market_Favourite', {
                    MarketId: market.id,
                    MarketName: market.name,
                    new_state: rest.isFavourited ? 'favourite' : 'unfavourite',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderMarketFavouriteClicked"';
                logger.error(e as Error);
            }
        },
        marketToggled(context: AnalyticsContext, payload: MarketTogglePayload): void {
            const { eventId, market, ...rest } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Market_Collapse_Expand', {
                    MarketId: market.id,
                    MarketName: market.name,
                    new_state: rest.isExpanded ? 'expanded' : 'collapsed',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderMarketToggled"';
                logger.error(e as Error);
            }
        },
        legAdded(context: AnalyticsContext, payload: LegAddedPayload): void {
            const { eventId, market, odd, ...rest } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Leg_Add', {
                    MarketId: market.id,
                    MarketName: market.name,
                    Odd: odd.price.toFixed(2),
                    Pick: odd.name,
                    position: rest.position,
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderLegAdded"';
                logger.error(e as Error);
            }
        },
        legRemoved(context: AnalyticsContext, payload: LegPayload): void {
            const { eventId, market, odd } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Leg_Remove', {
                    MarketId: market.id,
                    MarketName: market.name,
                    Odd: odd.price.toFixed(2),
                    Pick: odd.name,
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderLegRemoved"';
                logger.error(e as Error);
            }
        },
        uncombinableOddTapped(context: AnalyticsContext, payload: LegPayload): void {
            const { eventId, market, odd } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Leg_Uncombinable_Tap', {
                    MarketId: market.id,
                    MarketName: market.name,
                    Odd: odd.price.toFixed(2),
                    Pick: odd.name,
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderUncombinableOddTapped"';
                logger.error(e as Error);
            }
        },
        summaryCleared(context: AnalyticsContext, payload: SummaryPayload): void {
            const { eventId, summary } = payload;
            const { oddValue, oddName, numberOfLegs, isActive } = flattenBetBuilderSummary(summary);

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Summary_Clear', {
                    Pick: oddName,
                    Odd: oddValue.toFixed(2),
                    number_of_legs: numberOfLegs,
                    condition: isActive ? 'active' : 'blocked',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderSummaryCleared"';
                logger.error(e as Error);
            }
        },
        unavailableOddsRemoved(context: AnalyticsContext, payload: SummaryRemoveUnavailablePayload): void {
            const { eventId, summary } = payload;
            const { oddValue, oddName, numberOfLegs, isActive } = flattenBetBuilderSummary(summary);

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Unavailable_Remove', {
                    Pick: oddName,
                    Odd: oddValue.toFixed(2),
                    number_of_legs: numberOfLegs,
                    Number: payload.numberOfRemoved,
                    condition: isActive ? 'active' : 'blocked',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderUnavailableRemove"';
                logger.error(e as Error);
            }
        },
        superbetsCardClicked(context: AnalyticsContext, payload: SuperbetCardPayload): void {
            const { eventId, index } = payload;

            try {
                AnalyticsSDK.getInstance().log('Superbets_Event', {
                    Position: (index + 1).toString(),
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.superbetsCardClicked"';
                logger.error(e as Error);
            }
        },
        addSuperbetToBetslip(context: AnalyticsContext, payload: SuperbetBetslipPayload): void {
            const { eventId, index, odd: { name, value }, source } = payload;

            try {
                AnalyticsSDK.getInstance().log('Superbets_Betslip_Event_Add', {
                    Pick: name,
                    Odd: value.toFixed(2),
                    Position: index + 1,
                    Location: superbetsSourceMapper(source),
                    ...constructPayload(context, eventId, { position: index + 1 }),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.superbetsBetslipAdd"';
                logger.error(e as Error);
            }
        },
        addToBetslip(context: AnalyticsContext, payload: SummaryPayload): void {
            const { eventId, summary } = payload;
            const { oddValue, oddName, numberOfLegs, isActive } = flattenBetBuilderSummary(summary);

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Betslip_Event_Add', {
                    Pick: oddName,
                    Odd: oddValue.toFixed(2),
                    number_of_legs: numberOfLegs,
                    condition: isActive ? 'active' : 'blocked',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderBetslipAdd"';
                logger.error(e as Error);
            }
        },
        addToBetslipSucceeded(context: AnalyticsContext, payload: SummaryPayload): void {
            const { eventId, summary } = payload;
            const { oddValue, oddName, numberOfLegs, isActive } = flattenBetBuilderSummary(summary);

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Betslip_Event_Add', {
                    Pick: oddName,
                    Odd: oddValue.toFixed(2),
                    number_of_legs: numberOfLegs,
                    condition: isActive ? 'active' : 'blocked',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderBetslipAddSuccess"';
                logger.error(e as Error);
            }
        },
        addToBetslipFailed(context: AnalyticsContext, payload: SummaryPayload): void {
            const { eventId, summary } = payload;
            const { oddValue, oddName, numberOfLegs, isActive } = flattenBetBuilderSummary(summary);

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Betslip_Event_Add_Failed', {
                    Pick: oddName,
                    Odd: oddValue.toFixed(2),
                    number_of_legs: numberOfLegs,
                    condition: isActive ? 'active' : 'blocked',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderBetslipAddFailure"';
                logger.error(e as Error);
            }
        },
        betslipEventRemoved(context: AnalyticsContext, payload: BetslipBetRemovePayload): void {
            const { eventId, marketName, oddValue } = payload;
            const legs = marketName.split(';');

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Betslip_Event_Remove', {
                    Pick: marketName,
                    Odd: oddValue.toFixed(2),
                    number_of_legs: legs.length,
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderBetslipEventRemoved"';
                logger.error(e as Error);
            }
        },
        conflictOccurred(context: AnalyticsContext, payload: ConflictPayload): void {
            const { eventId, isNewSelectionBetBuilder } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Conflicting_Selections', {
                    New: isNewSelectionBetBuilder ? 'is_bb' : 'is_not_bb',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderConflictingSelections"';
                logger.error(e as Error);
            }
        },
        conflictReplaced(context: AnalyticsContext, payload: ConflictPayload): void {
            const { eventId, isNewSelectionBetBuilder } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Conflicting_Selections_R', {
                    New: isNewSelectionBetBuilder ? 'is_bb' : 'is_not_bb',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderConflictingSelectionsReplaced"';
                logger.error(e as Error);
            }
        },
        conflictCancelled(context: AnalyticsContext, payload: ConflictPayload): void {
            const { eventId, isNewSelectionBetBuilder } = payload;

            try {
                AnalyticsSDK.getInstance().log('Bet_Builder_Conflicting_Selections_R', {
                    New: isNewSelectionBetBuilder ? 'is_bb' : 'is_not_bb',
                    ...constructPayload(context, eventId),
                });
            } catch (e) {
                (e as Error).message += 'Resulted in not calling "Analytics.BetBuilder.betBuilderConflictingSelectionsCancelled"';
                logger.error(e as Error);
            }
        },
        tutorialPageTriggered(context: AnalyticsContext, title: string): void {
            AnalyticsSDK.getInstance().log('Tutorial', { title });
        },
    },
};

export type BetBuilderAnalyticsStore = typeof betBuilderAnalyticsStore;

type AnalyticsContext = ActionContext<Record<string, never>, unknown>;

type MatchDetailsOddsPayload = {
    eventId: number;
    hasSuperbets: boolean;
    hasBetBuilder: boolean;
};

type MarketGroupPayload = {
    eventId: number;
    marketGroupId: number;
    marketGroupName: string;
};

type MarketInfoPayload = {
    eventId: number;
    market: BetBuilderMarket;
    isOpened: boolean;
};

type MarketFavouritePayload = {
    eventId: number;
    market: Pick<BetBuilderMarket, 'id' | 'name'>;
    isFavourited: boolean;
};

type MarketTogglePayload = {
    eventId: number;
    market: Pick<BetBuilderMarket, 'id' | 'name'>
    isExpanded: boolean;
};

type LegPayload = {
    eventId: number;
    market: BetBuilderMarket;
    odd: BetBuilderOdd;
};

type LegAddedPayload = LegPayload & {
    position: number;
};

type SummaryPayload = {
    eventId: number;
    summary: BetBuilderPreviewSummary;
};

type BetslipBetRemovePayload = {
    eventId: number;
    marketName: string;
    oddValue: number;
};

type SummaryRemoveUnavailablePayload = SummaryPayload & {
    numberOfRemoved: number;
};

type ConflictPayload = {
    eventId: number;
    isNewSelectionBetBuilder: boolean;
};

type EventCardPayload = {
    eventId: number;
    source: EventCardSource;
};

type MatchInfoDto = {
    matchId: number;
    sportId: number;
    tournamentId: number;
    status: OfferStatus;
};

type SuperbetCardPayload = {
    eventId: number;
    index: number;
};

type SuperbetBetslipPayload = SuperbetCardPayload & {
    source: SelectionSourceScreen;
    odd: Pick<Odd, 'name' | 'value'>;
};

function constructPayload<T extends Record<string, any>>(context: AnalyticsContext, eventId: number, extra?: T) {
    const metadata = getMatchAnalyticsMetadata(context, eventId);

    const parsedMetada = {
        sport_id: `ax:sport:${metadata.sportId}`,
        tournament_id: `ax:tournament:${metadata.tournamentId}`,
        match_id: getAnalyticsEventId(metadata.matchId),
        status: metadata.status,
    };

    // TS is unable to process that the `metadata` object variable will override same key props defined in `extra`
    // Therefore we need to cast the return type here explicitly
    return { ...extra, ...parsedMetada };
}

function getMatchAnalyticsMetadata(context: AnalyticsContext, eventId: number): MatchInfoDto {
    const eventMap = (context.rootGetters as RelativeRootGetters)['data/sportOffer/eventsMap'];
    const event = eventMap[eventId.toString()];

    if (!event) {
        throw new Error(`Unable to generate match analytics metadata. Event by id ${eventId} was not found via root getter "data/sportOffer/eventsMap"`);
    }

    return {
        matchId: event.id,
        sportId: event.sportId,
        tournamentId: event.tournamentId,
        status: event.isOngoing() ? OfferStatus.Live : OfferStatus.Prematch,
    };
}

function flattenBetBuilderSummary(summary: BetBuilderPreviewSummary) {
    return {
        oddValue: summary.price,
        oddName: summary.legs.map((leg) => leg.name).join(';'),
        numberOfLegs: summary.legs.length,
        isActive: summary.status === BetBuilderOddStatus.Active,
    };
}
